'use client'

import { ChevronRight, Pin, PinOff, type LucideIcon } from 'lucide-react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { buildSidebarTitle } from '@/constant/locale-labels'
import type { Feature, Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import { useLocale } from '@/hooks/use-locale'
import { usePermissions } from '@/hooks/use-permissions'
import { usePinnedSidebar } from '@/hooks/use-pinned-sidebar'
import useSidebarSearch from '@/hooks/use-sidebar-search'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'

/** A menu entry below the top level. Groups can nest further entries to any depth. */
type SubMenuItem = {
  title: string
  url: string
  /** Extra pathnames that should also highlight this item (e.g. add/edit pages). */
  activeUrls?: string[]
  icon?: LucideIcon
  role?: Role[]
  feature?: Feature
  items?: SubMenuItem[]
}

type MenuItem = SubMenuItem & {
  hidden?: boolean
  isActive?: boolean
}

interface NavMainProps {
  items: MenuItem[]
  title?: string
}

/**
 * Pin button component for menu items
 */
function PinButton({ title, url }: { title: string; url: string }) {
  const { isPinned, togglePin } = usePinnedSidebar()
  const pinned = isPinned(url)

  // Don't show pin button for hash URLs (parent items without real routes)
  if (url === '#') return null

  return (
    <SidebarMenuAction
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        togglePin({ title, url })
      }}
      className='opacity-0 group-hover/sub:opacity-100 transition-opacity bg-background hover:bg-background rounded-full [&>svg]:size-3 top-1 cursor-pointer '
      title={pinned ? 'Unpin' : 'Pin'}
    >
      {pinned ? <PinOff className='h-2 w-2' /> : <Pin className='h-2 w-2' />}
      <span className='sr-only'>
        {pinned ? 'Unpin' : 'Pin'} {title}
      </span>
    </SidebarMenuAction>
  )
}

/**
 * Recursively determines whether an item would actually render anything for the current user.
 * A leaf renders when the user can navigate to it; a group (one that has `items`) renders only when
 * at least one of its descendants renders. This prevents a parent group (e.g. "Governance") from
 * showing an empty header when none of its nested items are accessible - a shallow check on the
 * immediate sub-item's own role/feature misses groups that have no own feature but gated children.
 */
type NavItemLike = { role?: Role[]; feature?: Feature; items?: NavItemLike[] }

function isItemRenderable(item: NavItemLike, canNavigate: (role?: Role[], feature?: Feature) => boolean): boolean {
  if (!canNavigate(item.role, item.feature)) return false
  if (item.items && item.items.length > 0) return item.items.some((child) => isItemRenderable(child, canNavigate))

  return true
}

/**
 * Normalizes an `activeUrls` entry for comparison against `location.pathname`:
 * strips any query string / hash and ensures a leading slash.
 */
const normalizePath = (url: string): string => {
  const path = url.split(/[?#]/)[0]
  return path.startsWith('/') ? path : `/${path}`
}

/**
 * Helper to check if a menu item matches the current pathname. Matches the item's own url
 * exactly, plus any pathname listed in `activeUrls` as a prefix - so a single entry like
 * '/ctra/engagements/add' also highlights nested routes such as
 * '/ctra/engagements/add/<uuid>?step=basics' (the query string is ignored).
 */
const isPathActive = (url: string | undefined, pathname: string, activeUrls?: string[]): boolean => {
  if (!!url && url !== '#' && pathname === url) return true
  return (
    activeUrls?.some((activeUrl) => {
      const target = normalizePath(activeUrl)
      return pathname === target || pathname.startsWith(`${target}/`)
    }) ?? false
  )
}

/**
 * Helper function to check if an item or any of its descendants is active
 */
function checkItemActive(item: SubMenuItem, pathname: string): boolean {
  if (isPathActive(item.url, pathname, item.activeUrls)) return true
  return item.items?.some((child) => checkItemActive(child, pathname)) ?? false
}

/**
 * Helper function to check if a URL is a real route
 */
const isRealRoute = (url?: string) => url && url !== '#'

/**
 * Component for rendering collapsible sub-item with proper state management
 */
function SubItemCollapsible({
  subItem,
  isSubAllowed,
  isSubItemActive,
  pathname,
  canNavigate,
  getDisplayTitle,
}: {
  subItem: SubMenuItem
  isSubAllowed: boolean
  isSubItemActive: boolean
  pathname: string
  canNavigate: (role?: Role[], feature?: Feature) => boolean
  getDisplayTitle: (title: string) => string
}) {
  const [isOpen, setIsOpen] = useState(isSubItemActive)
  const displayTitle = getDisplayTitle(subItem.title)

  // Sync state when pathname changes
  useEffect(() => {
    setIsOpen(isSubItemActive)
  }, [isSubItemActive])

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild className='group/collapsible'>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {isRealRoute(subItem?.url) ? (
            <Link to={isSubAllowed ? (subItem?.url ?? '#') : '#'}>
              <SidebarMenuButton
                tooltip={displayTitle}
                isActive={isPathActive(subItem.url, pathname, subItem.activeUrls)}
                className={!isSubAllowed ? 'cursor-default' : 'cursor-pointer'}
              >
                {subItem?.icon && <subItem.icon />}
                <span className='truncate' title={displayTitle}>
                  {displayTitle}
                </span>
                {subItem?.items && subItem.items.length > 0 && (
                  <ChevronRight className={`ml-auto transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                )}
              </SidebarMenuButton>
            </Link>
          ) : (
            <SidebarMenuButton
              tooltip={displayTitle}
              isActive={isPathActive(subItem.url, pathname, subItem.activeUrls)}
              className={!isSubAllowed ? 'cursor-default' : 'cursor-pointer'}
            >
              {subItem?.icon && <subItem.icon />}
              <span className='truncate' title={displayTitle}>
                {displayTitle}
              </span>
              {subItem?.items && subItem.items.length > 0 && (
                <ChevronRight className={`ml-auto transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
              )}
            </SidebarMenuButton>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItem.items?.map((child) => (
              <SubMenuItemRenderer
                key={child.title}
                subItem={child}
                canNavigate={canNavigate}
                pathname={pathname}
                getDisplayTitle={getDisplayTitle}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

/**
 * Component for rendering menu items below the top level. Groups render their children
 * with this same component, so nesting works at any depth.
 */
function SubMenuItemRenderer({
  subItem,
  canNavigate,
  pathname,
  getDisplayTitle,
}: {
  subItem: SubMenuItem
  canNavigate: (role?: Role[], feature?: Feature) => boolean
  pathname: string
  getDisplayTitle: (title: string) => string
}) {
  // Hides items the user can't open, and groups with no openable descendant.
  if (!isItemRenderable(subItem, canNavigate)) return null

  const isSubAllowed = canNavigate(subItem?.role, subItem?.feature)
  const displayTitle = getDisplayTitle(subItem.title)
  const isSubItemActive = checkItemActive(subItem, pathname)

  // Render collapsible sub-item if it has children
  if (subItem.items && subItem.items.length > 0) {
    return (
      <SubItemCollapsible
        key={subItem.title}
        subItem={subItem}
        isSubAllowed={isSubAllowed}
        isSubItemActive={isSubItemActive}
        pathname={pathname}
        canNavigate={canNavigate}
        getDisplayTitle={getDisplayTitle}
      />
    )
  }

  // Render simple sub-item
  return (
    <SidebarMenuSubItem key={subItem.title} className='group/sub '>
      <SidebarMenuSubButton asChild isActive={isSubItemActive} title={displayTitle}>
        <Link to={subItem.url ?? '#'}>
          {subItem.icon && <subItem.icon />}
          <span title={displayTitle}>{displayTitle}</span>
        </Link>
      </SidebarMenuSubButton>
      <PinButton title={displayTitle} url={subItem.url} />
    </SidebarMenuSubItem>
  )
}

/**
 * Component for rendering main menu items
 */
function MainMenuItemRenderer({
  item,
  canNavigate,
  pathname,
  isSearching,
  getDisplayTitle,
}: {
  item: MenuItem
  canNavigate: (role?: Role[], feature?: Feature) => boolean
  pathname: string
  isSearching: boolean
  getDisplayTitle: (title: string) => string
}) {
  if (item.hidden) return null

  const displayTitle = getDisplayTitle(item.title)
  const isAllowed = canNavigate(item?.role, item?.feature)
  const allowedSubItems = item.items?.filter((subItem) => isItemRenderable(subItem, canNavigate)) ?? []
  const hasValidSubItems = allowedSubItems.length > 0

  // Parent groups (e.g. url '#') should not render if user can't access any child.
  // This also prevents empty groups during search.
  const shouldShowItem = isRealRoute(item.url) ? isAllowed || (isSearching && hasValidSubItems) : hasValidSubItems

  if (!shouldShowItem) return null

  const isItemActive = checkItemActive(item, pathname)

  return (
    <Collapsible
      key={item.title}
      asChild
      defaultOpen={item.isActive || isItemActive || isSearching}
      className='group/collapsible'
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {isRealRoute(item?.url) ? (
            <Link to={isAllowed ? (item?.url ?? '#') : '#'}>
              <SidebarMenuButton
                tooltip={displayTitle}
                isActive={isPathActive(item.url, pathname, item.activeUrls)}
                className={!isAllowed ? 'cursor-default' : 'cursor-pointer'}
              >
                {item?.icon && <item.icon />}
                <span className='truncate' title={displayTitle}>
                  {displayTitle}
                </span>
                {hasValidSubItems && (
                  <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                )}
              </SidebarMenuButton>
            </Link>
          ) : (
            <SidebarMenuButton
              tooltip={displayTitle}
              isActive={isPathActive(item.url, pathname, item.activeUrls)}
              className={!isAllowed ? 'cursor-default' : 'cursor-pointer'}
            >
              {item?.icon && <item.icon />}
              <span className='truncate' title={displayTitle}>
                {displayTitle}
              </span>
              {hasValidSubItems && (
                <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
              )}
            </SidebarMenuButton>
          )}
        </CollapsibleTrigger>
        {hasValidSubItems && (
          <CollapsibleContent>
            <SidebarMenuSub>
              {allowedSubItems.map((subItem) => (
                <SubMenuItemRenderer
                  key={subItem.title}
                  subItem={subItem}
                  canNavigate={canNavigate}
                  pathname={pathname}
                  getDisplayTitle={getDisplayTitle}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function NavMain({ items, title = 'Menu' }: NavMainProps) {
  const { t } = useLocale()
  const location = useLocation()
  const { activeOrg } = useActiveOrg()
  const { canNavigate } = usePermissions()
  const { filterMenuItems, isSearching } = useSidebarSearch()

  const getDisplayTitle = (title: string) => buildSidebarTitle(title, t)

  const filteredItems = filterMenuItems(items)

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{title}</SidebarGroupLabel>
      <SidebarMenu>
        {filteredItems?.map((item, index) => {
          if (activeOrg?.parentId && item?.url === `/manage-organisation`) return null
          return (
            <MainMenuItemRenderer
              key={`${item.title}-${index}`}
              item={item}
              canNavigate={canNavigate}
              pathname={location.pathname}
              isSearching={isSearching}
              getDisplayTitle={getDisplayTitle}
            />
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
